import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, BellRing, CheckCircle2, Lock, PackageCheck, RefreshCw, Search, ShoppingCart, TrendingDown } from "lucide-react";

// Stock Availability — Physical vs Reserved (against open Sales Orders) vs
// Available, each item's position against its Minimum Stock Level, what's
// already in the procurement pipeline, and the MRP min-stock alert history.
// Backed by GET /api/stock/dashboard, /stock/reservations, /stock/alerts
// (backend/server.py "Stock Reservation against Sales Orders" section).

const STATUS = {
  below_min: { label: "Below Min", cls: "bg-red-50 text-red-700 border-red-200" },
  at_min: { label: "At Min", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  above_min: { label: "Above Min", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  no_min: { label: "No Min Set", cls: "bg-slate-50 text-slate-500 border-slate-200" },
};

const ALERT_STATUS = {
  open: "bg-red-50 text-red-700 border-red-200",
  superseded: "bg-slate-50 text-slate-500 border-slate-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "below_min", label: "Below Min" },
  { key: "at_min", label: "At Min" },
  { key: "above_min", label: "Above Min" },
  { key: "reserved", label: "Reserved" },
  { key: "available", label: "Available" },
  { key: "procurement", label: "Pending Procurement" },
];

const fmt = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 3 }));
const fmtDt = (v) => (v ? new Date(v).toLocaleString() : "—");

function Pill({ cls, children }) {
  return <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap ${cls}`}>{children}</span>;
}

function Tile({ icon: Icon, label, value, tone, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-md border px-3 py-2.5 transition ${tone} ${active ? "ring-2 ring-offset-1 ring-slate-400" : "hover:brightness-95"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] opacity-80">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="mono mt-1 text-xl font-bold">{value ?? 0}</div>
    </button>
  );
}

export default function StockAvailabilityPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canRunCheck = ["admin", "planner", "production_manager", "store_manager"].includes(user?.role);
  const [dash, setDash] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [type, setType] = useState("__all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, r, a] = await Promise.all([
        api.get("/stock/dashboard", { params: type === "__all" ? {} : { type } }),
        api.get("/stock/reservations"),
        api.get("/stock/alerts", { params: { limit: 300 } }),
      ]);
      setDash(d.data);
      setReservations(r.data || []);
      setAlerts(a.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load stock availability");
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (dash?.rows || []).filter((r) => {
      if (q && !`${r.code} ${r.name} ${r.supplier}`.toLowerCase().includes(q)) return false;
      switch (filter) {
        case "below_min": case "at_min": case "above_min": return r.status === filter;
        case "reserved": return r.reserved_qty > 0;
        case "available": return r.available_qty > 0;
        case "procurement": return r.pending_pr_qty > 0 || r.pending_po_qty > 0 || r.uncovered_requirement > 0;
        default: return true;
      }
    });
  }, [dash, filter, search]);

  const types = useMemo(() => [...new Set((dash?.rows || []).map((r) => r.type).filter(Boolean))].sort(), [dash]);

  const runCheck = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/stock/alerts/run");
      if (data.enabled === false) toast.info("Min-stock alerts are disabled in settings");
      else toast.success(`Checked ${data.checked} item(s) · ${data.new + data.repeat} new alert(s) · ${data.suppressed} already alerted · ${data.resolved} resolved`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Check failed");
    } finally {
      setRunning(false);
    }
  };

  const resolveAlert = async (id) => {
    try {
      await api.post(`/stock/alerts/${id}/resolve`);
      toast.success("Alert resolved");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not resolve alert");
    }
  };

  const s = dash?.summary || {};
  const tile = (key) => ({ active: filter === key, onClick: () => setFilter(filter === key ? "all" : key) });

  return (
    <div className="space-y-6" data-testid="stock-availability-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Inventory</div>
          <h1 className="text-2xl font-extrabold text-slate-900 mt-1">Stock Availability</h1>
          <p className="text-sm text-slate-500 mt-1">
            Available = Physical Stock − Stock reserved against open Sales Order. Minimum-stock alerts run with every MRP run.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="stock-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          {canRunCheck && (
            <Button onClick={runCheck} disabled={running} data-testid="stock-run-check-btn">
              <BellRing className="w-4 h-4 mr-1.5" /> {running ? "Checking…" : "Run Min-Stock Check"}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="stock-summary">
        <Tile icon={TrendingDown} label="Below Min" value={s.below_min} tone="bg-red-50 border-red-200 text-red-800" testid="tile-below-min" {...tile("below_min")} />
        <Tile icon={AlertTriangle} label="At Min" value={s.at_min} tone="bg-amber-50 border-amber-200 text-amber-800" testid="tile-at-min" {...tile("at_min")} />
        <Tile icon={CheckCircle2} label="Above Min" value={s.above_min} tone="bg-emerald-50 border-emerald-200 text-emerald-800" testid="tile-above-min" {...tile("above_min")} />
        <Tile icon={Lock} label="Reserved Items" value={s.reserved_items} tone="bg-purple-50 border-purple-200 text-purple-800" testid="tile-reserved" {...tile("reserved")} />
        <Tile icon={PackageCheck} label="Available Items" value={s.available_items} tone="bg-sky-50 border-sky-200 text-sky-800" testid="tile-available" {...tile("available")} />
        <Tile icon={ShoppingCart} label="Pending Procurement" value={s.pending_procurement_items} tone="bg-indigo-50 border-indigo-200 text-indigo-800" testid="tile-procurement" {...tile("procurement")} />
        <Tile icon={BellRing} label="Open Alerts" value={s.open_alerts} tone="bg-rose-50 border-rose-200 text-rose-800" testid="tile-open-alerts" active={false} onClick={() => {}} />
      </div>

      <Tabs defaultValue="position">
        <TabsList>
          <TabsTrigger value="position" data-testid="tab-position">Stock Position</TabsTrigger>
          <TabsTrigger value="reservations" data-testid="tab-reservations">Reservations ({reservations.length})</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">Alert History ({alerts.length})</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>}
        </TabsList>

        <TabsContent value="position" className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input className="pl-8 w-64" placeholder="Search code, name, supplier" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="stock-search" />
            </div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-40" data-testid="stock-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All item types</SelectItem>
                {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-1 flex-wrap">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={`text-xs px-2.5 py-1 rounded-full border ${filter === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`stock-filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-2 px-3">Code</th>
                  <th className="py-2 px-3">Item</th>
                  <th className="py-2 px-3">Type</th>
                  <th className="py-2 px-3 text-right">Physical</th>
                  <th className="py-2 px-3 text-right bg-purple-50/60">Reserved</th>
                  <th className="py-2 px-3">Reserved Against</th>
                  <th className="py-2 px-3 text-right bg-sky-50/60">Available</th>
                  <th className="py-2 px-3 text-right">Min Level</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3 text-right">Pending PR / PO</th>
                  <th className="py-2 px-3 text-right">Requirement</th>
                  <th className="py-2 px-3">Supplier</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.item_id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`stock-row-${r.code}`}>
                    <td className="py-2 px-3 mono text-slate-700">{r.code}</td>
                    <td className="py-2 px-3 text-slate-800">{r.name}</td>
                    <td className="py-2 px-3 text-xs text-slate-500">{r.type}</td>
                    <td className="py-2 px-3 mono text-right">{fmt(r.physical_stock)}</td>
                    <td className="py-2 px-3 mono text-right bg-purple-50/40">{fmt(r.reserved_qty)}</td>
                    <td className="py-2 px-3 text-[11px] text-purple-800">
                      {r.reservations.slice(0, 3).map((x) => (
                        <div key={`${x.so_id}-${x.order_no}`} title={x.customer_name}>
                          <span className="mono font-semibold">{x.order_no}</span>
                          {x.sales_order_codes?.length > 0 && <span className="text-slate-500"> ({x.sales_order_codes.join(", ")})</span>}: {fmt(x.reserved_qty)}
                          {x.customer_name && <div className="text-slate-600 truncate max-w-[220px]">{x.customer_name}</div>}
                        </div>
                      ))}
                      {r.reservations.length > 3 && <div className="text-slate-500">+{r.reservations.length - 3} more</div>}
                    </td>
                    <td className="py-2 px-3 mono text-right bg-sky-50/40 font-semibold">
                      {fmt(r.available_qty)}
                      {r.shortfall_qty > 0 && <div className="text-[10px] text-red-600 font-semibold">Short {fmt(r.shortfall_qty)}</div>}
                    </td>
                    <td className="py-2 px-3 mono text-right">{r.min_stock_level ? fmt(r.min_stock_level) : "—"}</td>
                    <td className="py-2 px-3">
                      <Pill cls={STATUS[r.status].cls}>{STATUS[r.status].label}</Pill>
                      {r.open_alert && <div className="text-[10px] text-rose-600 mt-0.5">Alerted · {r.open_alert.email_status}</div>}
                    </td>
                    <td className="py-2 px-3 mono text-right text-indigo-700" title={r.procurement_refs.join(", ")}>
                      {r.pending_pr_qty || r.pending_po_qty ? `${fmt(r.pending_pr_qty)} / ${fmt(r.pending_po_qty)}` : "—"}
                    </td>
                    <td className="py-2 px-3 mono text-right">
                      {r.procurement_requirement > 0 ? (
                        <span className={r.uncovered_requirement > 0 ? "text-red-600 font-bold" : "text-slate-600"} title={`Uncovered after pending PR/PO: ${r.uncovered_requirement}`}>
                          {fmt(r.procurement_requirement)}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="py-2 px-3 text-xs text-slate-600">{r.supplier || "—"}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={12} className="py-8 text-center text-sm text-slate-500">{loading ? "Loading…" : "No items in this view."}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="reservations">
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-2 px-3">Item</th>
                  <th className="py-2 px-3">Order No</th>
                  <th className="py-2 px-3">Sales Order</th>
                  <th className="py-2 px-3">Customer</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3 text-right">Ordered</th>
                  <th className="py-2 px-3 text-right">Delivered</th>
                  <th className="py-2 px-3 text-right bg-purple-50/60">Reserved</th>
                  <th className="py-2 px-3">Raised By</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r) => (
                  <tr key={`${r.so_id}-${r.line_no}`} className="border-t border-slate-100" data-testid={`reservation-row-${r.order_no}-${r.line_no}`}>
                    <td className="py-2 px-3"><span className="mono text-slate-700">{r.item_code}</span> <span className="text-slate-500 text-xs">{r.item_name}</span></td>
                    <td className="py-2 px-3 mono font-semibold">{r.order_no}</td>
                    <td className="py-2 px-3 mono text-xs">{r.sales_order_codes?.join(", ") || "—"}</td>
                    <td className="py-2 px-3 text-slate-700">{r.customer_name || "—"}</td>
                    <td className="py-2 px-3 text-xs text-slate-500">{r.status}</td>
                    <td className="py-2 px-3 mono text-right">{fmt(r.ordered_qty)}</td>
                    <td className="py-2 px-3 mono text-right">{fmt(r.delivered_qty)}</td>
                    <td className="py-2 px-3 mono text-right bg-purple-50/40 font-semibold">{fmt(r.reserved_qty)} <span className="text-slate-400 text-[10px]">{r.uom}</span></td>
                    <td className="py-2 px-3 text-xs text-slate-500">{r.created_by || "—"}</td>
                  </tr>
                ))}
                {reservations.length === 0 && (
                  <tr><td colSpan={9} className="py-8 text-center text-sm text-slate-500">No stock is reserved against open orders.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="alerts">
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-2 px-3">Alert Date / Time</th>
                  <th className="py-2 px-3">Item</th>
                  <th className="py-2 px-3 text-right">Previous Avail</th>
                  <th className="py-2 px-3 text-right">Physical</th>
                  <th className="py-2 px-3 text-right">Reserved</th>
                  <th className="py-2 px-3 text-right">Current Avail</th>
                  <th className="py-2 px-3 text-right">Min Level</th>
                  <th className="py-2 px-3 text-right">Reorder Qty</th>
                  <th className="py-2 px-3">Supplier</th>
                  <th className="py-2 px-3">Email Recipients</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100 align-top" data-testid={`alert-row-${a.id}`}>
                    <td className="py-2 px-3 text-xs whitespace-nowrap">
                      {fmtDt(a.created_at)}
                      <div className="text-[10px] text-slate-400">{a.trigger === "mrp" ? "MRP run" : "Manual check"}{a.alert_kind === "repeat" ? " · repeat" : ""}</div>
                    </td>
                    <td className="py-2 px-3"><span className="mono text-slate-700">{a.item_code}</span><div className="text-xs text-slate-500">{a.item_name}</div></td>
                    <td className="py-2 px-3 mono text-right text-slate-500">{fmt(a.previous_available_qty)}</td>
                    <td className="py-2 px-3 mono text-right">{fmt(a.physical_stock)}</td>
                    <td className="py-2 px-3 mono text-right">{fmt(a.reserved_qty)}</td>
                    <td className="py-2 px-3 mono text-right font-semibold text-red-600">{fmt(a.available_qty)}</td>
                    <td className="py-2 px-3 mono text-right">{fmt(a.min_stock_level)}</td>
                    <td className="py-2 px-3 mono text-right">{fmt(a.reorder_qty)}</td>
                    <td className="py-2 px-3 text-xs">
                      {a.supplier_name || "—"}
                      {a.supplier_contact?.phone && <div className="text-slate-500">{a.supplier_contact.phone}</div>}
                    </td>
                    <td className="py-2 px-3 text-[11px] text-slate-600 max-w-[220px]">
                      <div><span className="text-slate-400">To:</span> {(a.email_to || []).join(", ") || "—"}</div>
                      {(a.email_cc || []).length > 0 && <div><span className="text-slate-400">Cc:</span> {a.email_cc.join(", ")}</div>}
                      <div className={a.email_status === "failed" ? "text-red-600" : "text-slate-400"} title={a.email_error || ""}>Email: {a.email_status}</div>
                    </td>
                    <td className="py-2 px-3">
                      <Pill cls={ALERT_STATUS[a.status] || ALERT_STATUS.superseded}>{a.status}</Pill>
                      {a.resolved_at && <div className="text-[10px] text-slate-400 mt-0.5">{fmtDt(a.resolved_at)}</div>}
                    </td>
                    <td className="py-2 px-3">
                      {a.status === "open" && canRunCheck && (
                        <Button size="sm" variant="outline" onClick={() => resolveAlert(a.id)} data-testid={`alert-resolve-${a.id}`}>Resolve</Button>
                      )}
                    </td>
                  </tr>
                ))}
                {alerts.length === 0 && (
                  <tr><td colSpan={12} className="py-8 text-center text-sm text-slate-500">No minimum-stock alerts yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="settings">
            <StockSettings initial={dash?.settings} onSaved={load} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function StockSettings({ initial, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [cc, setCc] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      setCfg(initial);
      setCc((initial.alert_cc || []).join(", "));
    }
  }, [initial]);

  if (!cfg) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/stock/settings", {
        ...cfg,
        realert_drop_pct: Number(cfg.realert_drop_pct || 0),
        alert_cc: cc.split(",").map((x) => x.trim()).filter(Boolean),
      });
      setCfg(data);
      setCc((data.alert_cc || []).join(", "));
      toast.success("Stock control settings saved");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-5 max-w-3xl" data-testid="stock-settings">
      <div>
        <h3 className="font-semibold text-slate-900">Sales Order stock reservation</h3>
        <div className="mt-2 grid sm:grid-cols-2 gap-4">
          <div>
            <Label>When an order exceeds available stock</Label>
            <Select value={cfg.so_stock_check === "warn" ? "block" : cfg.so_stock_check} onValueChange={(v) => setCfg({ ...cfg, so_stock_check: v })}>
              <SelectTrigger data-testid="setting-so-check"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="block">Accept it, hold as Blocked · Awaiting stock</SelectItem>
                <SelectItem value="reject">Reject the order</SelectItem>
                <SelectItem value="off">Don't check</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1">Applies to orders raised with "Block stock". Accepted short orders hold whatever stock is free and wait for the rest, first come first served — physical stock and other orders' reservations are never changed. Orders raised "Without blocking" are never checked.</p>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <h3 className="font-semibold text-slate-900">MRP minimum stock alerts</h3>
        <div className="mt-2 grid sm:grid-cols-2 gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={cfg.alerts_enabled} onCheckedChange={(v) => setCfg({ ...cfg, alerts_enabled: v })} data-testid="setting-alerts-enabled" />
            Raise alerts on each MRP run
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={cfg.email_alerts_enabled} onCheckedChange={(v) => setCfg({ ...cfg, email_alerts_enabled: v })} data-testid="setting-email-enabled" />
            Email the supplier (CC list below)
          </label>
          <div>
            <Label>Alert when Available Stock is</Label>
            <Select value={cfg.alert_condition} onValueChange={(v) => setCfg({ ...cfg, alert_condition: v })}>
              <SelectTrigger data-testid="setting-condition"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lte">At or below minimum (≤)</SelectItem>
                <SelectItem value="lt">Below minimum (&lt;)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Re-alert an open item when stock drops a further (% of minimum)</Label>
            <Input type="number" min="0" value={cfg.realert_drop_pct} onChange={(e) => setCfg({ ...cfg, realert_drop_pct: e.target.value })} data-testid="setting-realert-pct" />
          </div>
          <div className="sm:col-span-2">
            <Label>CC recipients (comma separated)</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} data-testid="setting-cc" />
            <p className="text-[11px] text-slate-500 mt-1">The To address is the supplier's email from the Supplier Master, matched to the item's Supplier by name or code. When there's no supplier email, the alert goes to this list only.</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="stock-settings-save">{saving ? "Saving…" : "Save Settings"}</Button>
      </div>
    </div>
  );
}
